import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
} from '@mui/material';
import {
  CloudUpload,
  Image as ImageIcon,
  CheckCircle,
  Error as ErrorIcon,
  Clear,
  Info,
  Description,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { FaImage, FaMagic, FaInfoCircle, FaCheckCircle, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { useTheme } from '@mui/material/styles';
import { useProcess } from '../context/ProcessContext';
import HelpInfoIcon from '../components/HelpInfoIcon';
import InterfaceHeader from '../components/InterfaceHeader';
import FavoriteButton from '../components/FavoriteButton';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const RemoveBackground = () => {
  const theme = useTheme();
  const { processes, addProcess, updateProcess, removeProcess } = useProcess();
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // State management
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [apiCallsLeft, setApiCallsLeft] = useState(null);
  const [loadingApiCalls, setLoadingApiCalls] = useState(true);
  const [processedFiles, setProcessedFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [fileStatuses, setFileStatuses] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  // Lightbox state (AI Image Generator style)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Generate preview URLs when files change
  useEffect(() => {
    const urls = selectedFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [selectedFiles]);

  // Arrow key handler for thumbnail scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        container.scrollBy({ left: -160, behavior: 'smooth' });
      } else if (e.key === 'ArrowRight') {
        container.scrollBy({ left: 160, behavior: 'smooth' });
      }
    };
    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [selectedFiles]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setLightboxIndex(prev => (prev - 1 + lightboxImages.length) % lightboxImages.length);
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex(prev => (prev + 1) % lightboxImages.length);
      } else if (e.key === 'Escape') {
        setLightboxOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxImages.length]);

  // Fetch API calls left on component mount
  useEffect(() => {
    testBackendConnection();
    fetchApiCallsLeft();
  }, []);

  // Test backend connection
  const testBackendConnection = async () => {
    try {
      const data = await apiCall(API_ENDPOINTS.REMOVE_BACKGROUND_TEST, {
        method: 'GET',
      });

      console.log('Backend connection test successful:', data);
    } catch (err) {
      console.error('Backend connection test error:', err);
    }
  };

  const fetchApiCallsLeft = async () => {
    try {
      const data = await apiCall(API_ENDPOINTS.REMOVE_BACKGROUND_GET_API_CALLS_LEFT, {
        method: 'GET',
      });

      setApiCallsLeft(data.callsLeft);
    } catch (err) {
      console.error('Error fetching API calls left:', err);
      setApiCallsLeft('Error');
    } finally {
      setLoadingApiCalls(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processSelectedFiles(files);
  };

  // Maximum file size: 10MB
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

  // Common function to process selected files (used by both click and drag/drop)
  const processSelectedFiles = (files) => {
    const imageFiles = files.filter(file =>
      file.type === 'image/png' ||
      file.type === 'image/jpeg' ||
      file.type === 'image/jpg'
    );

    // Filter out files that exceed 10MB
    const validSizeFiles = imageFiles.filter(file => file.size <= MAX_FILE_SIZE);
    const oversizedFiles = imageFiles.filter(file => file.size > MAX_FILE_SIZE);

    let errorMessage = null;
    if (imageFiles.length !== files.length && oversizedFiles.length > 0) {
      errorMessage = `Some files were skipped: unsupported format or exceeds 10MB limit. ${oversizedFiles.map(f => f.name).join(', ')} exceeded the size limit.`;
    } else if (imageFiles.length !== files.length) {
      errorMessage = 'Some files were skipped. Only PNG, JPG, and JPEG files are supported.';
    } else if (oversizedFiles.length > 0) {
      errorMessage = `The following files exceed the 10MB limit and were skipped: ${oversizedFiles.map(f => f.name).join(', ')}`;
    }

    if (errorMessage) {
      setError(errorMessage);
    } else {
      setError(null);
    }

    setSelectedFiles(prev => [...prev, ...validSizeFiles]);
    setProcessedFiles([]);
    setFileStatuses({});
    setCurrentFileIndex(0);
  };

  // Drag and drop handlers
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = Array.from(event.dataTransfer.files);
    processSelectedFiles(files);
  };

  // Process all selected images
  const handleProcessImages = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image file.');
      return;
    }

    setProcessing(true);
    setError(null);
    setProcessedFiles([]);
    setFileStatuses({});
    setCurrentFileIndex(0);

    const processId = addProcess({
      name: 'Remove Background Processing',
      status: 'running',
      startTime: Date.now(),
    });

    try {
      const newStatuses = {};
      const processedResults = [];

      // Process each file
      for (let i = 0; i < selectedFiles.length; i++) {
        setCurrentFileIndex(i);
        const file = selectedFiles[i];
        
        try {
          // Update status to processing
          newStatuses[file.name] = 'processing';
          setFileStatuses({ ...newStatuses });

          // Create FormData
          const formData = new FormData();
          formData.append('image', file);

          // Send request to backend
          const response = await apiCall(API_ENDPOINTS.REMOVE_BACKGROUND_REMOVE, {
            method: 'POST',
            body: formData,
          });

          // Get processed image
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          // Get filename from response header
          const disposition = response.headers.get('Content-Disposition');
          let processedFileName = `${file.name.split('.')[0]}_NO_BACKGROUND.png`;
          if (disposition) {
            const filenameMatch = disposition.match(/filename[^;=\\n]*=((['\"]).*?\\2|[^;\\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              processedFileName = filenameMatch[1].replace(/['\"]/g, '');
            }
          }

          // Create URL for original image to show in comparison
          const originalUrl = URL.createObjectURL(file);

          processedResults.push({
            originalName: file.name,
            processedName: processedFileName,
            url: url,
            originalUrl: originalUrl,
            blob: blob,
          });

          newStatuses[file.name] = 'success';
          setFileStatuses({ ...newStatuses });

        } catch (err) {
          console.error(`Error processing ${file.name}:`, err);
          newStatuses[file.name] = 'error';
          setFileStatuses({ ...newStatuses });
          
          processedResults.push({
            originalName: file.name,
            error: err.message,
          });
        }

        // Add delay between API calls to respect rate limits
        if (i < selectedFiles.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      setProcessedFiles(processedResults);
      updateProcess(processId, { status: 'success' });

      // Refresh API calls left
      await fetchApiCallsLeft();

      // Auto-download all successfully processed files
      const successfulFiles = processedResults.filter(r => !r.error);
      successfulFiles.forEach((file, index) => {
        // Stagger downloads slightly to avoid browser blocking
        setTimeout(() => {
          const link = document.createElement('a');
          link.href = file.url;
          link.download = file.processedName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }, index * 300);
      });

      // Simple success notification (remove structured notification to avoid errors)
      console.log(`Successfully processed ${successfulFiles.length} of ${selectedFiles.length} images`);

      setTimeout(() => removeProcess(processId), 2000);

    } catch (err) {
      updateProcess(processId, { status: 'error', error: err.message });
      setError(`Error processing images: ${err.message}`);

      // Simple error logging (remove structured notification to avoid errors)
      console.error('Failed to process images:', err.message);

      setTimeout(() => removeProcess(processId), 5000);
    } finally {
      setProcessing(false);
      setCurrentFileIndex(0);
    }
  };


  // Clear selection
  const handleClear = () => {
    setSelectedFiles([]);
    setProcessedFiles([]);
    setFileStatuses({});
    setError(null);
    setCurrentFileIndex(0);
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxIndex(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove a single file from selection
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Scroll thumbnails left/right
  const scrollThumbnails = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: direction * 160, behavior: 'smooth' });
    }
  };

  // Open lightbox with images array and start index
  const openLightbox = (images, startIndex) => {
    setLightboxImages(images);
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  };

  // Open lightbox for selected file thumbnails
  const handlePreviewClick = (file, index) => {
    const images = selectedFiles.map((f, i) => ({ src: previewUrls[i], label: f.name }));
    openLightbox(images, index);
  };

  // Open lightbox for processed file comparison
  const handleImageClick = (file, processedIndex) => {
    const successFiles = processedFiles.filter(f => !f.error);
    // Build interleaved: original → processed for each file
    const images = [];
    const indexMap = {};
    successFiles.forEach((f, i) => {
      indexMap[`${i}_orig`] = images.length;
      images.push({ src: f.originalUrl, label: `Original — ${f.originalName}` });
      indexMap[`${i}_proc`] = images.length;
      images.push({ src: f.url, label: `Background Removed — ${f.originalName}` });
    });
    const startIdx = indexMap[`${processedIndex}_orig`] || 0;
    openLightbox(images, startIdx);
  };

  // Get status icon for file
  const getStatusIcon = (fileName) => {
    const status = fileStatuses[fileName];
    switch (status) {
      case 'processing':
        return <CircularProgress size={20} />;
      case 'success':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      default:
        return <ImageIcon sx={{ color: theme.palette.text.secondary }} />;
    }
  };

  // Calculate progress
  const progressValue = selectedFiles.length > 0 
    ? (currentFileIndex / selectedFiles.length) * 100 
    : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Remove Background" category="others" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          REMOVE BACKGROUND
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Background Removal
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* File Upload Section */}
              <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                    Upload Images
                  </Typography>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="image-upload"
                  />

                  {/* Drag and Drop Zone */}
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.OTHER.primary : selectedFiles.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      backgroundColor: isDragging
                        ? TOOL_COLORS.OTHER.light
                        : selectedFiles.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                      '&:hover': {
                        borderColor: TOOL_COLORS.OTHER.primary,
                        backgroundColor: TOOL_COLORS.OTHER.light,
                      },
                    }}
                  >
                    {selectedFiles.length > 0 ? (
                      <Box>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                          Click or drop more files to add
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <CloudUpload
                          sx={{
                            fontSize: 48,
                            color: isDragging ? TOOL_COLORS.OTHER.primary : theme.palette.text.disabled,
                            mb: 1,
                            transition: 'color 0.2s ease-in-out',
                          }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          {isDragging ? 'Drop images here' : 'Drag & drop images here'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                          or click to browse
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', mt: 1 }}>
                          PNG, JPG, JPEG (max 10MB per image)
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Scrollable Image Thumbnails */}
                  {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 1.5, position: 'relative' }}>
                      {selectedFiles.length > 3 && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => scrollThumbnails(-1)}
                            sx={{
                              position: 'absolute',
                              left: -4,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                              bgcolor: 'background.paper',
                              border: `1px solid ${theme.palette.divider}`,
                              boxShadow: 1,
                              '&:hover': { bgcolor: 'background.paper' },
                            }}
                          >
                            <ChevronLeft fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => scrollThumbnails(1)}
                            sx={{
                              position: 'absolute',
                              right: -4,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                              bgcolor: 'background.paper',
                              border: `1px solid ${theme.palette.divider}`,
                              boxShadow: 1,
                              '&:hover': { bgcolor: 'background.paper' },
                            }}
                          >
                            <ChevronRight fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <Box
                        ref={scrollContainerRef}
                        tabIndex={0}
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          overflowX: 'auto',
                          py: 1,
                          px: selectedFiles.length > 3 ? 3 : 0,
                          scrollBehavior: 'smooth',
                          '&::-webkit-scrollbar': { height: 6 },
                          '&::-webkit-scrollbar-thumb': {
                            bgcolor: theme.palette.divider,
                            borderRadius: 3,
                          },
                          '&:focus': { outline: 'none' },
                        }}
                      >
                        {selectedFiles.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              flexShrink: 0,
                              width: 140,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: 'background.paper',
                              cursor: 'pointer',
                              '&:hover': { borderColor: TOOL_COLORS.OTHER.primary },
                            }}
                            onClick={() => handlePreviewClick(file, index)}
                          >
                            {/* X remove button */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFile(index);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                zIndex: 1,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: '#fff',
                                p: 0.3,
                                '&:hover': { bgcolor: 'rgba(211,47,47,0.85)' },
                              }}
                            >
                              <Clear sx={{ fontSize: 14 }} />
                            </IconButton>
                            {/* Thumbnail image */}
                            <Box
                              sx={{
                                width: '100%',
                                height: 90,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                              }}
                            >
                              <img
                                src={previewUrls[index]}
                                alt={file.name}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                              />
                            </Box>
                            {/* File info bar */}
                            <Box sx={{ px: 0.75, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {fileStatuses[file.name] === 'processing' ? (
                                <CircularProgress size={14} />
                              ) : fileStatuses[file.name] === 'success' ? (
                                <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                              ) : fileStatuses[file.name] === 'error' ? (
                                <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                              ) : (
                                <Description sx={{ fontSize: 14, color: TOOL_COLORS.OTHER.primary }} />
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                  fontSize: '0.65rem',
                                }}
                              >
                                {file.name}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleProcessImages}
                  disabled={processing || selectedFiles.length === 0}
                  startIcon={processing ? <CircularProgress size={20} /> : <FaMagic />}
                  sx={{
                    bgcolor: TOOL_COLORS.OTHER.primary,
                    '&:hover': {
                      bgcolor: TOOL_COLORS.OTHER.hover,
                    },
                    px: 4
                  }}
                >
                  {processing ? 'Processing...' : 'Remove Backgrounds'}
                </Button>

                {(selectedFiles.length > 0 || processedFiles.length > 0) && (
                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    disabled={processing}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: 'rgba(0,0,0,0.05)',
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>

              {processing && (
                <Box sx={{ mt: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progressValue}
                    sx={{
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: TOOL_COLORS.OTHER.primary,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    Processing {currentFileIndex + 1} of {selectedFiles.length}
                  </Typography>
                </Box>
              )}

              {/* Results Section */}
              {processedFiles.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 3 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Processed Images
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Files are automatically downloaded after processing
                    </Typography>
                  </Box>

                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {processedFiles.map((file, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            {file.error ? <ErrorIcon color="error" /> : <CheckCircle color="success" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={file.originalName}
                            secondary={file.error || 'Background removed & downloaded'}
                          />
                        </ListItem>
                        {index < processedFiles.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Status & Information Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Information
              </Typography>

              {/* API Calls Display */}
              <Box sx={{ mb: 3 }}>
                <Chip
                  icon={<Info sx={{ color: `${TOOL_COLORS.OTHER.primary} !important` }} />}
                  label={loadingApiCalls ? 'Loading...' : `API Calls Left: ${apiCallsLeft}`}
                  variant="outlined"
                  sx={{ width: '100%', justifyContent: 'flex-start', borderColor: TOOL_COLORS.OTHER.primary, color: TOOL_COLORS.OTHER.primary }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Info Cards */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Key Information:
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <FaImage size={16} color={TOOL_COLORS.OTHER.primary} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Supported Formats"
                    secondary="PNG, JPG, JPEG files only"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Info sx={{ fontSize: 16, color: TOOL_COLORS.OTHER.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Max File Size"
                    secondary="10MB per image maximum"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <FaMagic size={16} color={TOOL_COLORS.OTHER.primary} />
                  </ListItemIcon>
                  <ListItemText
                    primary="AI-Powered"
                    secondary="Uses advanced AI to detect and remove backgrounds"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <FaCheckCircle size={16} color={TOOL_COLORS.OTHER.primary} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Output Format"
                    secondary="Results have transparent background (PNG)"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                <strong>1.</strong> Drag & drop or click to select images<br />
                <strong>2.</strong> Images must be under 10MB each<br />
                <strong>3.</strong> Click "Remove Backgrounds" to process<br />
                <strong>4.</strong> Files download automatically when done
              </Typography>

            </Paper>
          </Grid>
        </Grid>

        {/* Image Preview Section - Side by Side Comparison */}
      {processedFiles.filter(f => !f.error).length > 0 && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Divider sx={{ mb: 4 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
            Before & After Comparison (Click to enlarge)
          </Typography>
          <Box sx={{ maxHeight: '800px', overflowY: 'auto', px: 2 }}>
            {processedFiles.filter(f => !f.error).map((file, successIdx) => (
                <Paper
                  key={successIdx}
                  elevation={2}
                  sx={{
                    p: 3,
                    mb: 3,
                    border: `1px solid ${TOOL_COLORS.OTHER.light}`,
                    '&:hover': {
                      borderColor: TOOL_COLORS.OTHER.primary,
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, textAlign: 'center', color: theme.palette.text.primary }}>
                    {file.originalName}
                  </Typography>
                  <Grid container spacing={3}>
                    {/* Original Image */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip
                          label="Original"
                          size="small"
                          sx={{
                            mb: 1,
                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[600],
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                        <Box
                          sx={{
                            width: '100%',
                            height: 250,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                            borderRadius: 1,
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                            overflow: 'hidden'
                          }}
                        >
                          <img
                            src={file.originalUrl}
                            alt={`Original ${file.originalName}`}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleImageClick(file, successIdx)}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    {/* Processed Image */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip
                          label="Background Removed"
                          size="small"
                          sx={{
                            mb: 1,
                            bgcolor: TOOL_COLORS.OTHER.primary,
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                        <Box
                          sx={{
                            width: '100%',
                            height: 250,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                            borderRadius: 1,
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            backgroundImage: 'url("data:image/svg+xml,%3csvg width="100" height="100" xmlns="http://www.w3.org/2000/svg"%3e%3cdefs%3e%3cpattern id="smallGrid" width="8" height="8" patternUnits="userSpaceOnUse"%3e%3cpath d="M 8 0 L 0 0 0 8" fill="none" stroke="gray" stroke-width="0.5"/%3e%3c/pattern%3e%3cpattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse"%3e%3crect width="80" height="80" fill="url(%23smallGrid)"/%3e%3cpath d="M 80 0 L 0 0 0 80" fill="none" stroke="gray" stroke-width="1"/%3e%3c/pattern%3e%3c/defs%3e%3crect width="100%25" height="100%25" fill="url(%23grid)" /%3e%3c/svg%3e")',
                            backgroundSize: '20px 20px',
                            overflow: 'hidden'
                          }}
                        >
                          <img
                            src={file.url}
                            alt={`Processed ${file.originalName}`}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleImageClick(file, successIdx)}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Image Preview Lightbox (AI Image Generator style) */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.85)' } } }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton onClick={() => setLightboxOpen(false)}
            sx={{ position: 'absolute', top: -40, right: 0, color: 'white', zIndex: 1 }}>
            <FaTimes size={20} />
          </IconButton>

          {lightboxImages.length > 1 && (
            <IconButton
              onClick={() => setLightboxIndex(prev => (prev - 1 + lightboxImages.length) % lightboxImages.length)}
              sx={{ position: 'absolute', left: -60, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <FaChevronLeft size={24} />
            </IconButton>
          )}

          {lightboxImages[lightboxIndex] && (
            <Box sx={{ textAlign: 'center' }}>
              <img src={lightboxImages[lightboxIndex].src} alt={lightboxImages[lightboxIndex].label}
                style={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }} />
              <Typography sx={{ color: 'white', mt: 1, fontSize: '0.9rem' }}>
                {lightboxImages[lightboxIndex].label}
                {lightboxImages.length > 1 && ` (${lightboxIndex + 1}/${lightboxImages.length})`}
              </Typography>
            </Box>
          )}

          {lightboxImages.length > 1 && (
            <IconButton
              onClick={() => setLightboxIndex(prev => (prev + 1) % lightboxImages.length)}
              sx={{ position: 'absolute', right: -60, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <FaChevronRight size={24} />
            </IconButton>
          )}
        </Box>
      </Dialog>
      </Box>
    </Box>
  );
};

export default RemoveBackground;